import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import { serviceToDisplay } from "../../util/dateUtils";
import { LAUNCH_ASIN_TYPES } from "../../constants/launchAsinTypes";
import React from "react";
import {
  displayAttributeCell,
  displayMapAttributeWithMainKeyCell,
  displayValidationAttributeCell,
  displayValidationSummaryCell,
  formattedRestrictedPorAsinProgramName,
  formattedBundleDescription,
} from "../porAsinList/PorAsinListTable-config";
import AsinDisplayCell from "../common/AsinDisplayCell";
import { convertMapWithMainKeyToString } from "../../util/mapUtils";

export const asinTableColumnDefinitions = {
  title: "Select visible columns",
  options: [
    {
      label: "ASIN Attributes",
      options: [
        {
          id: "announceDate",
          label: "Announce Date",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "onlineStreetDate",
          label: "Online Street Date",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "programName",
          label: "Program",
          editable: false,
          defaultVisible: true,
        },
        {
          id: "marketplace",
          label: "Marketplace",
          editable: false,
          defaultVisible: true,
        },
        { id: "asin", label: "ASIN", editable: false, defaultVisible: true },
        {
          id: "asinParty",
          label: "ASIN Partiness",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "asinType",
          label: "ASIN Type",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "baselineStatus",
          label: "Baseline Status",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "userDescription",
          label: "User Description",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "description",
          label: "Description",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "partNumber",
          label: "Part Number",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "productLineName",
          label: "Product Line Name",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bundleBaseComponent",
          label: "Bundle Base Component",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "catalogLiveness",
          label: "Catalog Liveness",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "liveness",
          label: "Liveness",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bundleComponents",
          label: "Bundle Components",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bundleComponentQuantity",
          label: "Bundle Component Quantity",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "productLineId",
          label: "Product Line Id",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "itemType",
          label: "Item Type",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "salesChannel",
          label: "Sales Channel",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "programId",
          label: "Program Id",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "upc",
          label: "UPC",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "dtcp",
          label: "DTCP",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "productionFamily",
          label: "Production Family",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "gtin",
          label: "GTIN",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "allowListedDeviceCode",
          label: "Allow Listed Device Code",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "allowListedDtid",
          label: "Allow Listed DTID",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bomDtids",
          label: "BOM DTID(s)",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "domainName",
          label: "Domain Name",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "makoWeblabStatus",
          label: "Weblab Status",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "buyBoxStatus",
          label: "BuyBox Status",
          editable: true,
          defaultVisible: false,
        },
      ],
    },
    {
      label: "ASIN Validation Attributes",
      options: [
        {
          id: "validationStatus",
          label: "Validation Status Summary",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultHattrick",
          label: "Hattrick Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultLVS",
          label: "LVS Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultManualDGL",
          label: "Manual DGL Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultManualRL",
          label: "Manual RL Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultManualInstock",
          label: "Manual Instock Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultManualOX",
          label: "Manual OX Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultManualGTM",
          label: "Manual GTM Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultManualCS",
          label: "Manual CS Validation Result",
          editable: true,
          defaultVisible: true,
        },
      ],
    },
  ],
};
// The order of columns is different between the two tables.
export const VALIDATION_COLUMN_DEFINITIONS: any = [
  {
    id: "programName",
    header: "Program",
    cell: (item: FullAsinWithAudits | RestrictedAsin) =>
      item.isRestricted ? (
        <span
          style={{ fontStyle: "italic" }}
          children={formattedRestrictedPorAsinProgramName(item)}
        />
      ) : (
        displayAttributeCell("programName", item)
      ),
    sortingField: "programName",
  },
  {
    id: "marketplace",
    header: "Marketplace",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("marketplace", item),
  },
  {
    id: "asin",
    header: "ASIN",
    cell: (item: FullAsinWithAudits) => (
      <AsinDisplayCell item={item} componentName={"validationTable"} />
    ),
  },
  {
    id: "validationStatus",
    header: "Validation Status Summary",
    cell: (item: FullAsinWithAudits) =>
      displayValidationSummaryCell("validationStatus", item),
  },
  {
    id: "validationOverallResultHattrick",
    header: "Hattrick Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultHattrick",
        "validationDetailResultHattrick",
        item
      ),
    sortingField: "validationOverallResultHattrick",
  },
  {
    id: "validationOverallResultLVS",
    header: "LVS Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultLVS",
        "validationDetailResultLVS",
        item
      ),
    sortingField: "validationOverallResultLVS",
  },
  {
    id: "validationOverallResultManualDGL",
    header: "DGL Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualDGL",
        "validationDetailResultManualDGL",
        item
      ),
    sortingField: "validationOverallResultManualDGL",
  },
  {
    id: "validationOverallResultManualRL",
    header: "RL Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualRL",
        "validationDetailResultManualRL",
        item
      ),
    sortingField: "validationOverallResultManualRL",
  },
  {
    id: "validationOverallResultManualInstock",
    header: "Instock Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualInstock",
        "validationDetailResultManualInstock",
        item
      ),
    sortingField: "validationOverallResultManualInstock",
  },
  {
    id: "validationOverallResultManualOX",
    header: "OX Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualOX",
        "validationDetailResultManualOX",
        item
      ),
    sortingField: "validationOverallResultManualOX",
  },
  {
    id: "validationOverallResultManualGTM",
    header: "GTM Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualGTM",
        "validationDetailResultManualGTM",
        item
      ),
    sortingField: "validationOverallResultManualGTM",
  },
  {
    id: "validationOverallResultManualCS",
    header: "CS Validation Result",
    cell: (item: FullAsinWithAudits) =>
      displayValidationAttributeCell(
        "validationOverallResultManualCS",
        "validationDetailResultManualCS",
        item
      ),
    sortingField: "validationOverallResultManualCS",
  },

  {
    id: "announceDate",
    header: "Announce Date",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("announceDate", item, serviceToDisplay),
    sortingField: "announceDate",
  },
  {
    id: "onlineStreetDate",
    header: "Online Street Date",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("onlineStreetDate", item, serviceToDisplay),
    sortingField: "onlineStreetDate",
  },
  {
    id: "asinParty",
    header: "ASIN Partiness",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("asinParty", item),
    sortingField: "asinParty",
  },
  {
    id: "asinType",
    header: "ASIN Type",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("asinType", item, (v) => LAUNCH_ASIN_TYPES[v] || v),
    sortingField: "asinType",
  },
  {
    id: "baselineStatus",
    header: "Baseline Status",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("baselineStatus", item),
    sortingField: "baselineStatus",
  },
  {
    id: "userDescription",
    header: "User Description",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("userDescription", item),
    sortingField: "userDescription",
  },
  {
    id: "description",
    header: "Description",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("description", item, (v) =>
        ["soft_bundle", "vmp_bundle"].includes(item.asinType)
          ? formattedBundleDescription(v)
          : v
      ),
    sortingField: "description",
  },
  {
    id: "partNumber",
    header: "Part Number",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("partNumber", item),
    sortingField: "partNumber",
  },
  {
    id: "productLineName",
    header: "Product Line Name",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("productLineName", item),
    sortingField: "productLineName",
  },
  {
    id: "bundleBaseComponent",
    header: "Bundle Base Component",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("bundleBaseComponent", item),
    sortingField: "bundleBaseComponent",
  },
  {
    id: "catalogLiveness",
    header: "Catalog Liveness",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("catalogLiveness", item),
    sortingField: "catalogLiveness",
  },
  {
    id: "liveness",
    header: "Liveness",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("liveness", item),
    sortingField: "liveness",
  },
  {
    id: "bundleComponents",
    header: "Bundle Components",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("bundleComponents", item),
    sortingField: "bundleComponents",
  },
  {
    id: "bundleComponentQuantity",
    header: "Bundle Component Quantity",
    cell: (item: FullAsinWithAudits) =>
      displayMapAttributeWithMainKeyCell(
        "bundleComponentQuantity",
        item,
        convertMapWithMainKeyToString,
        "bundleBaseComponent"
      ),
    sortingField: "bundleComponents",
  },
  {
    id: "productLineId",
    header: "Product Line Id",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("productLineId", item),
    sortingField: "productLineId",
  },
  {
    id: "itemType",
    header: "Item Type",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("itemType", item),
    sortingField: "itemType",
  },
  {
    id: "salesChannel",
    header: "Sales Channel",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("salesChannel", item),
    sortingField: "salesChannel",
  },
  {
    id: "programId",
    header: "Program Id",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("programId", item),
    sortingField: "programId",
  },
  {
    id: "upc",
    header: "UPC",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("upc", item),
    sortingField: "upc",
  },
  {
    id: "dtcp",
    header: "DTCP",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("dtcp", item),
    sortingField: "dtcp",
  },
  {
    id: "productionFamily",
    header: "Production Family",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("productionFamily", item),
    sortingField: "productionFamily",
  },
  {
    id: "gtin",
    header: "GTIN",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("gtin", item),
    sortingField: "gtin",
  },
  {
    id: "allowListedDeviceCode",
    header: "Allow Listed Device Code",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("allowListedDeviceCode", item),
    sortingField: "allowListedDeviceCode",
  },
  {
    id: "allowListedDtid",
    header: "Allow Listed DTID",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("allowListedDtid", item),
    sortingField: "allowListedDtid",
  },
  {
    id: "bomDtids",
    header: "BOM DTID(s)",
    cell: (item: FullAsinWithAudits) => displayAttributeCell("bomDtids", item),
    sortingField: "bomDtids",
  },
  {
    id: "domainName",
    header: "Domain Name",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("domainName", item),
    sortingField: "domainName",
  },
  {
    id: "makoWeblabStatus",
    header: "Weblab Status",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("makoWeblabStatus", item),
    sortingField: "makoWeblabStatus",
  },
  {
    id: "buyBoxStatus",
    header: "BuyBox Status",
    cell: (item: FullAsinWithAudits) =>
      displayAttributeCell("buyBoxStatus", item),
    sortingField: "buyBoxStatus",
  },
];
